import { get } from 'lodash/fp';

import { TrackingConsent } from '@mews/enums';
import { TrackingConsents } from '@mews/framework';
import { TimeoutError } from '@mews/framework/errors';
import { createAction } from '@mews/framework/redux';
import { getCookieFirstToken } from '../../modules/configuration/selectors';
import { DistributorPromiseThunkAction, DistributorThunkAction } from '../../types';
import { initializeGainsightService } from '../gainsightService/gainsight';
import { initializeInstrumentation } from '../../observability/instrumentation';
import { TRACKING_CONSENTS_READY } from './actionTypes';

const trackingConsentsReady = createAction(TRACKING_CONSENTS_READY);

const updateCookieConsentsServices =
    (consents: TrackingConsents): DistributorThunkAction =>
    (dispatch) => {
        if (get(TrackingConsent.Performance, consents)) {
            void dispatch(initializeGainsightService());
            void dispatch(initializeInstrumentation());
        }
    };

const finalizeTrackingConsentsInitialization =
    (): DistributorThunkAction =>
    (dispatch, _getState, { trackingConsentsService }) => {
        const consents = trackingConsentsService.getAllConsents();
        dispatch(updateCookieConsentsServices(consents));
        return dispatch(trackingConsentsReady);
    };

export const prepareTrackingConsents =
    (): DistributorPromiseThunkAction =>
    async (dispatch, getState, { loggerService, trackingConsentsService }) => {
        const state = getState();
        const cookieFirstToken = getCookieFirstToken(state);

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (cookieFirstToken) {
            trackingConsentsService.subscribeToConsents((consents: TrackingConsents) => {
                dispatch(updateCookieConsentsServices(consents));
            });

            return trackingConsentsService
                .initAndOpenBanner(cookieFirstToken)
                .then(({ isAlreadyInitialized, processTime }) => {
                    if (!isAlreadyInitialized) {
                        loggerService.log(
                            'tracking-consents-processed',
                            'Tracking consents has been successfully processed.',
                            { processTime }
                        );
                    }

                    return dispatch(finalizeTrackingConsentsInitialization());
                })
                .catch((error: Error) => {
                    if (error instanceof TimeoutError) {
                        loggerService.log('tracking-consents-timeout', 'Tracking consents has timed out.', {
                            processTime: error.timeout,
                        });
                    } else {
                        loggerService.log(
                            'tracking-consents-init-error',
                            'Tracking consents service could not be initialized.',
                            { message: get('message', error) }
                        );
                    }

                    return dispatch(finalizeTrackingConsentsInitialization());
                });
        }

        return Promise.resolve(dispatch(finalizeTrackingConsentsInitialization()));
    };
